import { useSnapshot } from 'valtio';

import { cx } from '@libs/classnames';
import { formatNameInitials } from '@libs/formatNames';
import { Avatar } from '@ui/components/Avatar';
import { FancyBadge } from '@ui/components/FancyBadge';
import { MicIcon } from '@ui/icons/MicIcon';

import { RefereeInfo, Speaking } from '@features/video/types';
import { VideoPreview } from '@features/video/ui/VideoPreview';

import { NO_SPEAKING_PROXY } from './stores';
import { TileState } from './TileState';

import styles from './PlayerTile.module.css';

type Props = {
  className?: string;
  referee: RefereeInfo;
  // горячее: identity говорящих
  speakingProxy?: Speaking;
  // адрес приватной комнаты, где сейчас судья: другие судьи могут присоединиться
  privateRoomHref?: string;
};

// Плитка судьи: та же плитка, что у игрока, но камеры нет — только голос, поэтому вместо
// видео кружок с инициалами, как аватар в шапке, рамка говорящего и выключенный микрофон;
// действий нет. В привате — та же плашка, что у игрока в привате, с именем игрока
export function RefereeTile({
  className,
  referee,
  speakingProxy = NO_SPEAKING_PROXY,
  privateRoomHref,
}: Props) {
  const { name, isMe, micOn, privateWith } = referee;
  const isSpeaking = !!useSnapshot(speakingProxy)[referee.id];
  return (
    <div className={cx(styles.tile_wrap, className)}>
      <article className={cx(styles.tile, { [styles.is_private]: Boolean(privateWith) })}>
        <div className={styles.tile__media}>
          {privateWith ? (
            <TileState text="В приватной комнате" note={`Игрок: ${privateWith.name}`}>
              {privateRoomHref && (
                <FancyBadge href={privateRoomHref} inNewTab={false}>
                  Перейти
                </FancyBadge>
              )}
            </TileState>
          ) : (
            <>
              <VideoPreview
                className={cx(styles.tile__device, styles.is_voice, {
                  [styles.is_speaking]: isSpeaking,
                })}
                stream={null}
                placeholder={<Avatar size="72" text={initials(name)} />}
              />
              {micOn === false && (
                <span className={cx(styles.tile__mic, styles.is_off)} title="Микрофон выключен">
                  <MicIcon off />
                </span>
              )}
            </>
          )}
        </div>
        <footer className={styles.tile__caption}>
          <span className={styles.tile__name} title={name}>
            {name}
          </span>
          {isMe && <span className={styles.tile__me}>вы</span>}
        </footer>
      </article>
    </div>
  );
}

/*
    ____,-------------------------------,____
    \   |            Helpers            |   /
    /___|-------------------------------|___\
*/

// Имя судьи приходит из билета одной строкой: инициалы первых двух слов в их порядке
function initials(name: string): string {
  const [first, second] = name.trim().split(/\s+/);
  return formatNameInitials({ lastName: first, firstName: second });
}
